import type React from "react";

/**
 * CanadaMap — decorative hero background SVG
 *
 * Simplified province/territory outlines + animated city pins.
 * Not cartographically exact — designed for visual recognition only.
 *
 * ViewBox: 960 × 560
 * Coordinate mapping (approximate Mercator):
 *   x = (141 − lon_W) × 10.8
 *   y = (83  − lat_N) × 13.66
 */

const PROVINCES = [
  // Territories
  { id: "YT", d: "M 0,314 L 0,178 L 65,165 L 194,246 L 194,314 Z" },
  { id: "NT", d: "M 0,178 L 0,50 L 432,50 L 432,314 L 194,314 L 194,246 L 65,165 Z" },
  { id: "NU", d: "M 432,50 L 960,50 L 960,200 L 820,60 L 760,90 L 710,130 L 650,165 L 590,200 L 540,260 L 497,300 L 432,314 L 432,50 Z" },

  // Western provinces (nearly rectangular — accurate)
  { id: "BC", d: "M 0,314 L 194,314 L 227,314 L 227,464 L 105,474 L 30,438 L 0,396 Z" },
  { id: "AB", d: "M 227,314 L 335,314 L 335,464 L 227,464 Z" },
  { id: "SK", d: "M 335,314 L 432,314 L 432,464 L 335,464 Z" },
  { id: "MB", d: "M 432,314 L 497,300 L 497,442 L 454,464 L 432,464 Z" },

  // Central / Eastern
  { id: "ON", d: "M 497,300 L 540,260 L 568,314 L 588,388 L 598,458 L 588,510 L 554,540 L 518,525 L 488,498 L 454,464 L 497,442 L 497,300 Z" },
  { id: "QC", d: "M 568,314 L 590,200 L 650,165 L 710,130 L 744,180 L 758,328 L 748,428 L 718,468 L 682,498 L 648,514 L 618,518 L 598,510 L 588,510 L 598,458 L 588,388 L 568,314 Z" },

  // Atlantic
  { id: "NB", d: "M 744,468 L 766,450 L 782,473 L 769,493 L 746,492 Z" },
  { id: "NS", d: "M 782,473 L 820,468 L 838,488 L 812,508 L 784,502 Z" },
  { id: "PE", d: "M 778,460 L 794,456 L 798,464 L 784,468 Z" },
  // Labrador (mainland Newfoundland)
  { id: "NL-L", d: "M 758,278 L 798,238 L 856,276 L 846,373 L 798,402 L 760,386 Z" },
  // Newfoundland island
  { id: "NL-I", d: "M 892,340 L 918,320 L 936,348 L 920,374 L 898,368 Z" },
];

// Major Canadian cities — (lat, lon) → (x, y) via coordinate formula above
const CITIES = [
  { id: "van", name: "Vancouver",  x: 194, y: 461, active: true  },
  { id: "cal", name: "Calgary",    x: 292, y: 437, active: false },
  { id: "edm", name: "Edmonton",   x: 297, y: 403, active: true  },
  { id: "win", name: "Winnipeg",   x: 474, y: 452, active: false },
  { id: "tor", name: "Toronto",    x: 665, y: 525, active: true  },
  { id: "ott", name: "Ottawa",     x: 704, y: 512, active: false },
  { id: "mtl", name: "Montréal",   x: 728, y: 511, active: true  },
  { id: "hal", name: "Halifax",    x: 814, y: 502, active: false },
] as const;

// Connection lines between active cities (major coverage hubs)
const CONNECTIONS = [
  ["van", "edm"],
  ["edm", "win"],
  ["win", "tor"],
  ["tor", "ott"],
  ["ott", "mtl"],
] as const;

function getCityPos(id: string) {
  return CITIES.find((c) => c.id === id)!;
}

export function CanadaMap({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg
      viewBox="0 0 960 560"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      aria-hidden="true"
    >
      <defs>
        {/* Gradient for connection lines */}
        <linearGradient id="conn-grad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor="#3b82f6" stopOpacity="0" />
          <stop offset="50%"  stopColor="#3b82f6" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
        </linearGradient>

        {/* Glow filter for active pins */}
        <filter id="pin-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* ── Province fills + borders ── */}
      <g>
        {PROVINCES.map((p) => (
          <path
            key={p.id}
            d={p.d}
            fill="rgba(255,255,255,0.025)"
            stroke="rgba(255,255,255,0.10)"
            strokeWidth="0.8"
            strokeLinejoin="round"
          />
        ))}
      </g>

      {/* ── Connection lines ── */}
      <g>
        {CONNECTIONS.map(([a, b]) => {
          const ca = getCityPos(a);
          const cb = getCityPos(b);
          return (
            <line
              key={`${a}-${b}`}
              x1={ca.x} y1={ca.y}
              x2={cb.x} y2={cb.y}
              stroke="url(#conn-grad)"
              strokeWidth="1"
              strokeDasharray="4 6"
            >
              <animate
                attributeName="stroke-dashoffset"
                from="0" to="-100"
                dur="6s"
                repeatCount="indefinite"
              />
            </line>
          );
        })}
      </g>

      {/* ── City pins ── */}
      <g>
        {CITIES.map((city) => (
          <g key={city.id} transform={`translate(${city.x},${city.y})`}>
            {/* Outer pulse ring 1 */}
            <circle r="4" fill="none" stroke="#3b82f6" strokeWidth="1.5" opacity="0">
              <animate attributeName="r"       from="4"   to="20"  dur="2.4s" repeatCount="indefinite" begin={`${CITIES.indexOf(city) * 0.4}s`} />
              <animate attributeName="opacity" from="0.7" to="0"   dur="2.4s" repeatCount="indefinite" begin={`${CITIES.indexOf(city) * 0.4}s`} />
            </circle>

            {/* Outer pulse ring 2 (staggered) */}
            {city.active && (
              <circle r="4" fill="none" stroke="#60a5fa" strokeWidth="1" opacity="0">
                <animate attributeName="r"       from="4"   to="30"  dur="2.4s" repeatCount="indefinite" begin={`${CITIES.indexOf(city) * 0.4 + 1.2}s`} />
                <animate attributeName="opacity" from="0.5" to="0"   dur="2.4s" repeatCount="indefinite" begin={`${CITIES.indexOf(city) * 0.4 + 1.2}s`} />
              </circle>
            )}

            {/* Pin dot */}
            <circle
              r={city.active ? 4 : 3}
              fill={city.active ? "#3b82f6" : "#60a5fa"}
              opacity={city.active ? 1 : 0.65}
              filter={city.active ? "url(#pin-glow)" : undefined}
            />

            {/* City label */}
            <text
              x="0"
              y={city.active ? -8 : -6}
              textAnchor="middle"
              fontSize={city.active ? "8" : "7"}
              fill="rgba(255,255,255,0.65)"
              fontFamily="'Plus Jakarta Sans', sans-serif"
              fontWeight={city.active ? "600" : "400"}
              letterSpacing="0.3"
            >
              {city.name}
            </text>
          </g>
        ))}
      </g>
    </svg>
  );
}
